import React, { useEffect, useState } from "react";
import { Button, Dropdown, Input, Select, Tag, Tooltip } from "antd";
import Grid from "../../../../common/table";
import {
    Leave_Duration,
    Leave_Request_Field,
    Leave_Status,
    Leave_Type,
} from "../../../../constant/constant";
import { SearchParams } from "../../../../page-components/common/search-criteria/type/search-criteria.type";
import Locale from "../../../../core/locale/locale";
import dayjs from "dayjs";
import EmployeeAvatar from "../../../../page-components/employee/avatar/employee-avatar";
import { PlusCircleOutlined } from "@ant-design/icons";
import LeaveAuditDetail from "../common/leave-audit-detail";
import ApplyLeaveModal from "../common/leave-apply-modal";
import { useLeaveContext } from "../common/leaveContext";
import "../../../../styles/component/leave-styles.scss";
import LeaveReviewModal from "../common/leave-review-modal";
import { getName } from "../../../../utility/utility";
import server from "../../../../server";
import SearchCriteria from "../../../../page-components/common/search-criteria/search-criteria";
import { LeaveTypeSearchCriteria } from "../leave-request/components/utils";
import EmployeeOverviewPopover
    from "../../../../page-components/employee/overview/employee-overview-popover/employee-overview-popover";
import LoadingSkeleton from "../../../../page-components/common/loading-skeleton/loading-skeleton";
import { ClipboardCheck, ClipboardClock, Ellipsis } from "lucide-react";

const LeaveApplication: React.FC = () => {
    const [state, setState] = useState({
        leaveList: [],
        auditRecord: null,
        reviewModalConfig: {
            data: null,
            isOpen: false,
        },
        auditDetailsModal: {
            data: null,
            isOpen: false,
        },
        isApplyLeaveModalOpen: false,
        reason: '',
        leavePeriod: [],
    });

    const [searchParams, setSearchParams] = useState<SearchParams>({});
    const {supervisor, isAdmin} = useLeaveContext();
    const [pageNumber, setPageNumber] = useState(0);
    const [sortField, setSortField] = useState("CREATED_ON");
    const [sortDirection, setSortDirection] = useState<"ASC" | "DESC" | undefined>("DESC");
    const pageSize = 8;
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isAdmin || supervisor)
            fetchLeaveRequests(pageNumber, sortField, sortDirection);
        getAllLeavePeriod();
    }, [isAdmin, supervisor, pageNumber])

    const getAllLeavePeriod = async () => {
        const allLeavePeriods = await server.leave.period.list();
        const ActiveLeavePeriods = allLeavePeriods.filter((leavePeriod: any) => (leavePeriod.active === true));
        setState((prev) => ({
            ...prev,
            leavePeriod: ActiveLeavePeriods
        }));
    }

    const handleNextPage = () => {
        setPageNumber(pageNumber + 1);
    }

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            setPageNumber(pageNumber - 1);
        }
    };

    const fetchLeaveRequests = async (pageNumber: number, sortField: any, sortDirection: any) => {
        setLoading(true);
        try {
            let response;
            if (isAdmin) {
                const search = {...searchParams, sortBy: sortField, sortDirection: sortDirection};
                response = await server.leave.request.list(search, pageNumber, pageSize);
            } else if (supervisor?.isSupervisor && supervisor?.empId) {
                const payload = {
                    includeReporteesRequests: true,
                    sortBy: sortField,
                    sortDirection: sortDirection
                }
                response = await server.leave.request.list(payload, pageNumber, pageSize);
            } else {
                response = [];
            }

            const fullLeaveRequests = response.map((item: any, index: number) => {
                return {
                    ...item,
                    name: getName(item.employeeName.firstName, item.employeeName.middleName, item.employeeName.lastName),
                    reason: item.reason
                }
            })
            setState((prev) => ({
                ...prev,
                leaveList: fullLeaveRequests
            }))

        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false)
        }
    }

    const handleSortChange = async (field: string, direction: "ASC" | "DESC" | undefined) => {
        setSortField(field);
        setSortDirection(direction);
        setSearchParams({...searchParams, sortBy: field, sortDirection: direction});

        // reset to first page
        // setPageNumber(0);

        // fetch again with sorting + filters
        fetchLeaveRequests(pageNumber, field, direction);
    };

    function openReviewModal(record: any) {
        setState((prev) => (
            {
                ...prev,
                reviewModalConfig: {
                    data: record,
                    isOpen: true
                }
            }));
    }

    function closeReviewModal() {
        setState((prev) => (
            {
                ...prev,
                reviewModalConfig: {
                    data: null,
                    isOpen: false
                }
            }));
    }

    function openViewAuditModal(record: any) {
        setState((prev) => ({
            ...prev,
            auditRecord: {...record}
        }))
    }

    function setApplyLeaveModalOpen() {
        setState((prev) => ({
            ...prev,
            isApplyLeaveModalOpen: !prev.isApplyLeaveModalOpen
        }))
    }

    const handleSearch = async (params: SearchParams) => {
        setLoading(true);
        try {
            let payload: any = {...params, sortBy: sortField, sortDirection: sortDirection};

            if (params.duration && Array.isArray(params.duration)) {
                payload.duration = {
                    startDate: params.duration[0] ? dayjs(params.duration[0]).format(Locale.dateFormat) : null,
                    endDate: params.duration[1] ? dayjs(params.duration[1]).format(Locale.dateFormat) : null
                }
            }

            if (supervisor?.isSupervisor && !isAdmin) {
                payload = {
                    ...payload,
                    includeReporteesRequests: true
                };
            }
            const resp = await server.leave.request.list(
                payload,
                0,
                pageSize
            );
            const mappedLeaveList = resp.map((item: any) => ({
                ...item,
                name: getName(
                    item.employeeName.firstName,
                    item.employeeName.middleName,
                    item.employeeName.lastName
                ),
                reason: item.reason
            }));

            setPageNumber(0);
            setSearchParams(payload);
            setState((prev) => ({
                ...prev,
                leaveList: mappedLeaveList
            }));

        } catch (error) {
            console.error("Search failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Employee Name',
            dataIndex: 'name',
            align: "left",
            render: (_: any, record: any) => {
                return (
                    <div className="employee-name-wrapper">
                        <EmployeeAvatar
                            name={record.employeeName}
                            thumbnailId={record.profilePhotoThumbnailId}
                            size={30}
                        />
                        <EmployeeOverviewPopover
                            employeeId={record.employeeId}
                            uniqueKey={`${record.employeeId}-${record.id}`}
                        >
                            <div className="employee-search-table-name">
                                {record?.name}
                            </div>
                        </EmployeeOverviewPopover>
                    </div>
                );
            }

        }, {
            title: 'Leave Type',
            dataIndex: 'leaveType',
            align: "left",
            render: (record: string) => {
                return Leave_Type[record as keyof typeof Leave_Type]
            }
        },
        {
            title: 'Date',
            dataIndex: 'date',
            align: "left",
            sorter: true,
            sortField: Leave_Request_Field.date,
            sortOrder: sortField === Leave_Request_Field.date ?
                sortDirection === 'ASC' ? "ascend"
                    : sortDirection === 'DESC' ? 'descend'
                    : null :
                null,
            isDateFormat: true,
            render: (_: any, record: any) => record?.date ? dayjs(record.date).format(Locale.displayDate) : "-"
        },
        {
            title: 'Session',
            dataIndex: 'session',
            align: "left",
            isDataTypeConvertable: {
                isConvertable: true,
                leaveDataType: Leave_Duration,
            },
            render: (leaveDuration: string) => {
                return Leave_Duration[leaveDuration as keyof typeof Leave_Duration]
            }
        },
        {
            title: 'Leave Status',
            dataIndex: 'state',
            align: "left",
            sorter: true,
            sortField: Leave_Request_Field.state,
            sortOrder: sortField === Leave_Request_Field.state ?
                sortDirection === 'ASC' ? "ascend"
                    : sortDirection === 'DESC' ? 'descend'
                    : null :
                null,
            DataTypeConvertable: {
                isConvertable: true,
                leaveStatusDataTYpe: Leave_Status
            },
            render: (status: any) => (
                <>
                    {
                        <Tag className={`leave-status ${status.toLowerCase()}`}>
                            {status.toUpperCase()}
                        </Tag>
                    }
                </>
            ),
        },
        {
            title: 'Leave Purpose',
            dataIndex: 'reason',
            align: "left",
            render: (text: string) => (
                <Tooltip title={text} classNames={{root: "custom-tooltip"}}>
                    {text.length > 7 ? text.substring(0, 7) + '...' : text}
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            dataIndex: 'id',
            width:'5%',
            render: (_: any, record: any) => {
                 const menuItems=[
                     {
                         label:'Review',
                         onClick:()=>{openReviewModal(record)},
                         key:0,
                         icon:<ClipboardCheck className={'action-view'} />,

                     },
                     {
                         label:'Audit Log',
                         onClick:()=>{openViewAuditModal(record)},
                         key:1,
                         icon:<ClipboardClock  className={'action-view'}/>
                     }
                 ]
                return (
                    <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                        <Ellipsis className={'pn-table-actions-icon'}/>
                    </Dropdown>
                )
            }
        }
    ];

    return (
        <div className={"table-container leave-application"}>
            {
                (isAdmin || supervisor?.isSupervisor) &&
                <SearchCriteria
                    searchCriteria={LeaveTypeSearchCriteria(state.leavePeriod)}
                    onSearch={handleSearch}
                    showReset={true}
                    title={"Search Leaves"}
                    renderTopContent={({values, onChange}) => (
                        <>
                            <div>
                                <label className={"label"}>Name</label>
                                <Input
                                    placeholder="Enter name"
                                    value={values.employeeName || ''}
                                    onChange={(e) => onChange('employeeName', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className={"label"}>Employee Code</label>
                                <Input
                                    placeholder="Enter employee code"
                                    value={values.employeeCode || ''}
                                    onChange={(e) => onChange('employeeCode', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={"label"}>Select Status</label>
                                <Select
                                    placeholder={"Select status"}
                                    onChange={(val: any) => onChange('state', val)}
                                    value={values.state}
                                    allowClear
                                    className={"label"}
                                >
                                    {(Object.keys(Leave_Status) as Array<keyof typeof Leave_Status>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Leave_Status[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </div>
                        </>
                    )}
                />
            }
            {isAdmin &&
            <Button
                type="primary"
                icon={<PlusCircleOutlined/>}
                onClick={setApplyLeaveModalOpen}
                className={"add-data-btn"}
            >
                Apply Leave
            </Button>
            }
            <div className={'leave-tables'}>
                <LoadingSkeleton loading={loading}>
                    <Grid
                        bordered={true}
                        columns={columns}
                        dataSource={state.leaveList}
                        onChange={(pagination, filters, sorter) => {
                            const backendField = sorter.column?.sortField ?? Leave_Request_Field.createdOn;

                            if (!backendField) return;

                            const direction =
                                sorter.order === "ascend" ? "ASC" : "DESC";

                            handleSortChange(backendField, direction);
                        }}
                        paginationButton={true}
                        pagination={false}
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                        handleNextPage={handleNextPage}
                        handlePrevPage={handlePrevPage}
                    />
                </LoadingSkeleton>

            </div>

            {state.reviewModalConfig.data &&
            <LeaveReviewModal
                data={state.reviewModalConfig.data}
                isOpen={state.reviewModalConfig.isOpen}
                isAdmin={isAdmin}
                supervisor={supervisor}
                pageNumber={pageNumber}
                field={sortField}
                direction={sortDirection}
                handleModalClose={closeReviewModal}
                fetchLeaveRequests={fetchLeaveRequests}
            />}

            {state.auditRecord &&
            <LeaveAuditDetail
                record={state.auditRecord}
            />
            }

            {state.isApplyLeaveModalOpen &&
            <ApplyLeaveModal
                isOpen={state.isApplyLeaveModalOpen}
                selectedEmployee={null}
                setIsOpen={setApplyLeaveModalOpen}
                isAdmin={isAdmin}
                field={sortField}
                direction={sortDirection}
                pageNumber={pageNumber}
                fetchLeaveRequests={fetchLeaveRequests}
                title={'Apply Leave On Behalf'}
            />
            }

        </div>
    )
}

export default LeaveApplication;
